import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../firebase";

const CHUNK = 10; // Firestore array-contains-any limit

function chunkIds(ids, size = CHUNK) {
  const out = [];
  for (let i = 0; i < ids.length; i += size) {
    out.push(ids.slice(i, i + size));
  }
  return out;
}

/** Subcategory doc ids whose parent is `parentId`. */
export function getSubcategoryIds(allCategories, parentId) {
  return (allCategories || [])
    .filter((c) => c.parentCategory?.categoryId === parentId)
    .map((c) => c.id);
}

/**
 * Unique products whose `categories` includes any of the given ids.
 * Chunks array-contains-any and dedupes across chunks.
 */
export async function fetchProductsForCategoryIds(categoryIds) {
  const ids = [...new Set((categoryIds || []).filter(Boolean))];
  if (ids.length === 0) return [];

  const productsRef = collection(db, "products");
  const byId = new Map();

  if (ids.length === 1) {
    const snap = await getDocs(
      query(productsRef, where("categories", "array-contains", ids[0]))
    );
    snap.docs.forEach((d) => byId.set(d.id, { id: d.id, ...d.data() }));
    return [...byId.values()];
  }

  const snaps = await Promise.all(
    chunkIds(ids).map((part) =>
      getDocs(
        query(productsRef, where("categories", "array-contains-any", part))
      )
    )
  );
  snaps.forEach((snap) => {
    snap.docs.forEach((d) => byId.set(d.id, { id: d.id, ...d.data() }));
  });
  return [...byId.values()];
}

/** Category ids to query: subcategory → itself; parent → self + children. */
export function resolveCategoryFilterIds(category, allCategories = []) {
  if (!category?.id) return [];
  if (category.isSubcategory) return [category.id];
  const subs = getSubcategoryIds(allCategories, category.id);
  return [category.id, ...subs];
}

export async function fetchProductsForCategory(category, allCategories = []) {
  let catalog = allCategories;
  if (!category?.isSubcategory && catalog.length === 0) {
    const snap = await getDocs(collection(db, "categories"));
    catalog = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  }
  return fetchProductsForCategoryIds(
    resolveCategoryFilterIds(category, catalog)
  );
}

export async function countProductsForCategory(category, allCategories = []) {
  const products = await fetchProductsForCategory(category, allCategories);
  return products.length;
}

/** Attach live `productCount` (parent includes subcategories). */
export async function attachProductCounts(categories, allCategories) {
  let catalog = allCategories;
  if (!catalog || catalog.length === 0) {
    const snap = await getDocs(collection(db, "categories"));
    catalog = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  }
  return Promise.all(
    (categories || []).map(async (c) => {
      const productCount = await countProductsForCategory(c, catalog);
      return { ...c, productCount };
    })
  );
}
